import axios from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthCallPromise } from 'use-eazy-auth'
import { Regista, PaginatedDJResponse, Fonte } from '../types'

export function useFonti(params: Record<string, any> = {}) {
  return useQuery<PaginatedDJResponse<Fonte>>(
    ['fonti', params],
    useAuthCallPromise(
      (token: string) =>
        ({ signal }) =>
          axios
            .get(
              `/api/admin/fonti/?${new URLSearchParams(params).toString()}`,
              {
                signal,
                headers: { Authorization: `Bearer ${token}` },
              }
            )
            .then((r) => r.data)
    ),
    {
      keepPreviousData: true,
    }
  )
}

export function useFonte(id: number) {
  return useQuery<Fonte>(
    ['fonti', id],
    useAuthCallPromise(
      (token: string) =>
        ({ signal }) =>
          axios
            .get(`/api/admin/fonti/${id}/`, {
              signal,
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((r) => r.data)
    )
  )
}

export function useDeleteFonte() {
  const client = useQueryClient()
  return useMutation(
    useAuthCallPromise(
      (token: string) => (id: number) =>
        axios.delete(`/api/admin/fonti/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
    ),
    {
      onSuccess() {
        client.invalidateQueries(['fonti'])
      },
    }
  )
}

export function useCreateFonte() {
  const client = useQueryClient()
  return useMutation(
    useAuthCallPromise(
      (token: string) => (content: Fonte) =>
        axios
          .post(`/api/admin/fonti/`, content, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((r) => r.data as Fonte)
    ),
    {
      onSuccess(savedFonte) {
        client.invalidateQueries(['fonti'])
        client.setQueryData(['registe', savedFonte.id], savedFonte)
      },
    }
  )
}

export function useUpdateFonte() {
  const client = useQueryClient()
  return useMutation(
    useAuthCallPromise(
      (token: string) => (fonte: Fonte) =>
        axios
          .put(`/api/admin/fonti/${fonte.id}/`, fonte, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((r) => r.data as Fonte)
    ),
    {
      onSuccess(savedFonte) {
        client.invalidateQueries(['fonti'])
        client.setQueryData(['fonte', savedFonte.id], savedFonte)
      },
    }
  )
}

export function useUnificaFonti() {
  const client = useQueryClient()
  return useMutation(
    useAuthCallPromise(
      (token: string) =>
        ({
          fonte_to_keep,
          fonti_to_delete,
        }: {
          fonte_to_keep: number
          fonti_to_delete: number[]
        }) =>
          axios
            .post(
              `/api/admin/fonti/unifica/`,
              { fonte_to_keep, fonti_to_delete },
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            )
            .then((r) => r.data as Fonte[])
    ),
    {
      onSuccess() {
        client.invalidateQueries(['fonti'])
      },
    }
  )
}
